import axios from 'axios';
import { OrderData, CustomerData, ApiResponse, PaginatedResponse } from '../types/store.types';

// Configuração base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/orders`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar respostas de erro
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const orderService = {
  // Criar novo pedido
  async createOrder(orderData: {
    storeId: string;
    customer: CustomerData;
    items: any[];
    payment: any;
    shipping: any;
    notes?: string;
  }): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.post('/', orderData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar pedido');
    }
  },

  // Buscar pedido por ID
  async getOrderById(orderId: string): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.get(`/${orderId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar pedido');
    }
  },

  // Buscar pedido por número
  async getOrderByNumber(orderNumber: string): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.get(`/number/${orderNumber}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar pedido');
    }
  },

  // Listar pedidos de uma loja
  async getStoreOrders(
    storeId: string,
    page = 1,
    limit = 20,
    filters?: {
      status?: string;
      paymentStatus?: string;
      shippingStatus?: string;
      dateFrom?: string;
      dateTo?: string;
      customerEmail?: string;
      sortBy?: 'createdAt' | 'total' | 'status';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PaginatedResponse<OrderData>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await api.get(`/store/${storeId}?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao listar pedidos');
    }
  },

  // Listar pedidos do cliente
  async getCustomerOrders(
    page = 1,
    limit = 20,
    filters?: {
      status?: string;
      dateFrom?: string;
      dateTo?: string;
      sortBy?: 'createdAt' | 'total';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PaginatedResponse<OrderData>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await api.get(`/customer?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao listar pedidos');
    }
  },

  // Atualizar status do pedido
  async updateOrderStatus(
    orderId: string,
    status: string,
    notes?: string
  ): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.patch(`/${orderId}/status`, { status, notes });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar status do pedido');
    }
  },

  // Atualizar status de pagamento
  async updatePaymentStatus(
    orderId: string,
    status: string,
    transactionId?: string
  ): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.patch(`/${orderId}/payment`, {
        status,
        transactionId,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar status de pagamento');
    }
  },

  // Atualizar status de envio
  async updateShippingStatus(
    orderId: string,
    status: string,
    trackingCode?: string
  ): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.patch(`/${orderId}/shipping`, {
        status,
        trackingCode,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar status de envio');
    }
  },

  // Cancelar pedido
  async cancelOrder(orderId: string, reason?: string): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.patch(`/${orderId}/cancel`, { reason });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao cancelar pedido');
    }
  },

  // Reembolsar pedido
  async refundOrder(orderId: string, amount?: number, reason?: string): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.post(`/${orderId}/refund`, { amount, reason });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao reembolsar pedido');
    }
  },

  // Gerar nota fiscal
  async generateInvoice(orderId: string): Promise<Blob> {
    try {
      const response = await api.get(`/${orderId}/invoice`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao gerar nota fiscal');
    }
  },

  // Enviar email de confirmação
  async sendConfirmationEmail(orderId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(`/${orderId}/send-confirmation`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao enviar email de confirmação');
    }
  },

  // Obter estatísticas de pedidos
  async getOrderStats(storeId: string, period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<{
    totalOrders: number;
    totalRevenue: number;
    averageOrderValue: number;
    conversionRate: number;
    ordersByStatus: Record<string, number>;
    revenueByPeriod: Array<{ period: string; revenue: number }>;
  }>> {
    try {
      const response = await api.get(`/store/${storeId}/stats?period=${period}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar estatísticas');
    }
  },

  // Exportar pedidos
  async exportOrders(
    storeId: string,
    format: 'csv' | 'xlsx' = 'csv',
    filters?: {
      status?: string;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams({
        format,
        ...filters,
      });

      const response = await api.get(`/store/${storeId}/export?${params}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao exportar pedidos');
    }
  },

  // Buscar pedidos por rastreamento
  async trackOrder(trackingCode: string): Promise<ApiResponse<{
    order: OrderData;
    trackingHistory: Array<{
      status: string;
      location: string;
      date: Date;
      description: string;
    }>;
  }>> {
    try {
      const response = await api.get(`/track/${trackingCode}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao rastrear pedido');
    }
  },

  // Adicionar nota ao pedido
  async addOrderNote(orderId: string, note: string, isInternal = false): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.post(`/${orderId}/notes`, { note, isInternal });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao adicionar nota');
    }
  },

  // Obter histórico do pedido
  async getOrderHistory(orderId: string): Promise<ApiResponse<Array<{
    action: string;
    description: string;
    user: string;
    date: Date;
    metadata?: any;
  }>>> {
    try {
      const response = await api.get(`/${orderId}/history`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar histórico');
    }
  },

  // Duplicar pedido
  async duplicateOrder(orderId: string): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.post(`/${orderId}/duplicate`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao duplicar pedido');
    }
  },

  // Processar pagamento
  async processPayment(orderId: string, paymentData: any): Promise<ApiResponse<OrderData>> {
    try {
      const response = await api.post(`/${orderId}/process-payment`, paymentData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao processar pagamento');
    }
  },

  // Validar cupom de desconto
  async validateCoupon(couponCode: string, storeId: string, orderValue: number): Promise<ApiResponse<{
    valid: boolean;
    discount: number;
    discountType: 'percentage' | 'fixed';
    message: string;
  }>> {
    try {
      const response = await api.post('/validate-coupon', {
        couponCode,
        storeId,
        orderValue,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao validar cupom');
    }
  },

  // Calcular frete
  async calculateShipping(
    storeId: string,
    zipCode: string,
    items: Array<{ productId: string; quantity: number }>
  ): Promise<ApiResponse<Array<{
    method: string;
    cost: number;
    estimatedDays: number;
    description: string;
  }>>> {
    try {
      const response = await api.post('/calculate-shipping', {
        storeId,
        zipCode,
        items,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao calcular frete');
    }
  },
};

export default orderService;
